/* eslint-disable no-console, no-plusplus, prefer-arrow-callback, no-await-in-loop */
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET } from 'common/constants/pdfExport';
import { NetworkMonitor } from 'common/types/network';
import { Logger } from 'utilities';
import {
  CONVERSION_RATIOS,
  IMAGE_SETTINGS,
  PROGRESS_MILESTONES,
  PROGRESS_PERCENTAGE,
  TEMPLATE_CONSTANTS,
  TIMEOUT_MS,
} from './constants';
import { MultiplePagesExportOptions, MultiplePagesExportResult } from './interfaces';
import { SmoothProgressAnimator } from './SmoothProgressAnimator';

/**
 * PDF Exporter class for handling multiple pages export
 */
export class PdfExporter {
  private pdf: JsPDF | null = null;
  private logoDataUrl: string | null = null;
  private networkMonitor: NetworkMonitor;

  constructor() {
    this.networkMonitor = new NetworkMonitor();
  }

  /**
   * Wait for all API calls to complete and pdfContentRef element to be present and loaded
   */
  private waitForApiCallsAndPdfContentElement = async (
    document: Document,
    maxWaitTime: number = 60000
  ): Promise<void> =>
    new Promise<void>(resolve => {
      const startTime = Date.now();

      // Setup network monitoring
      const { hasPendingRequests: hasPendingMonitoredRequests, cleanup } = this.networkMonitor.setupNetworkMonitoring();

      const checkForApiCallsAndElement = () => {
        // Check for pending network requests using multiple methods
        const hasPendingViaPerformance = this.networkMonitor.hasPendingNetworkRequests();
        const hasPendingViaMonitoring = hasPendingMonitoredRequests();

        if (hasPendingViaPerformance || hasPendingViaMonitoring) {
          Logger.info('Network requests still pending, waiting...');
          setTimeout(checkForApiCallsAndElement, TIMEOUT_MS.API_CHECK_RETRY);
          return;
        }

        // Get the query parameter from the document's URL
        const documentUrl = document.location?.href || document.defaultView?.location?.href;
        const reference = documentUrl
          ? new URL(documentUrl).searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET)
          : '';

        // Look for element with data-ref attribute
        const targetElement = document.querySelector(`[data-ref="${reference}"]`);

        if (targetElement) {
          Logger.info('Found target element in the DOM, checking if content is loaded...');

          // Check if element has content (not just skeleton)
          const hasContent = targetElement.children.length > 0;

          // Check for skeleton elements that might still be visible
          const skeletonElements = document.querySelectorAll(
            '[data-testid*="skeleton"], [data-testid*="loading"], ' +
              '.skeleton, .loading-skeleton, .shimmer, ' +
              '[class*="skeleton"], [class*="loading"], ' +
              '[aria-label*="loading"], [aria-label*="skeleton"]'
          );

          const hasVisibleSkeletons = Array.from(skeletonElements).some(element => {
            const style = window.getComputedStyle(element);
            return (
              style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              style.opacity !== '0' &&
              (element as HTMLElement).offsetWidth > 0 &&
              (element as HTMLElement).offsetHeight > 0
            );
          });

          if (hasContent && !hasVisibleSkeletons) {
            Logger.info('Target element is loaded with content and no skeletons visible');
            Logger.info('Waiting 4 seconds for content to fully settle...');

            // Wait 4 seconds for content to fully settle after API calls are done
            setTimeout(() => {
              Logger.info('4-second delay completed, proceeding with PDF generation');
              cleanup(); // Clean up monitoring
              resolve();
            }, 4000);
            return;
          }

          if (hasVisibleSkeletons) {
            Logger.info('Skeleton elements still visible, waiting...');
          }
        }

        // Check if we've exceeded max wait time
        if (Date.now() - startTime > maxWaitTime) {
          Logger.warn('Timeout waiting for API calls and target element, proceeding anyway');
          cleanup(); // Clean up monitoring
          resolve();
          return;
        }

        // Check again in 500ms
        setTimeout(checkForApiCallsAndElement, 500);
      };

      checkForApiCallsAndElement();
    });

  /**
   * Load image from public folder and convert to base64 data URL
   */
  private static async loadImageAsDataUrl(imagePath: string): Promise<string> {
    try {
      const response = await fetch(imagePath);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      Logger.error('Error loading image:', error);
      throw error;
    }
  }

  /**
   * Generate cover page template
   */
  public generateCover(coverTitle?: string, entityName?: string, dateOfIssue?: string): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const halfHeight = this.pdf.internal.pageSize.getHeight() / 2;
    const xPosition = TEMPLATE_CONSTANTS.COVER_MARGIN_MM;
    const coverTitleY = halfHeight - TEMPLATE_CONSTANTS.COVER_MARGIN_MM;
    const entityNameY = halfHeight;
    const dateOfIssueY = halfHeight + TEMPLATE_CONSTANTS.COVER_MARGIN_MM * 2;

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(20);
    this.pdf.text(coverTitle || '', xPosition, coverTitleY);

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(24);
    this.pdf.text(entityName || '', xPosition, entityNameY);

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(16);
    this.pdf.text(dateOfIssue || '', xPosition, dateOfIssueY);
  }

  /**
   * Generate header template for a PDF page
   */
  public generateHeader(entityName?: string, pageTitle?: string): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const pageWidth = this.pdf.internal.pageSize.getWidth();

    // Add header to the top of the page
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.text(entityName || '', pageWidth / 2, TEMPLATE_CONSTANTS.PAGE_MARGIN_MM, { align: 'center' });
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(53, 123, 255);
    const lineWidth = pageWidth * 0.8;
    const lineStartX = pageWidth - lineWidth;
    const lineY = 15;
    this.pdf.line(lineStartX, lineY, lineStartX + lineWidth, lineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Add page title below the header
    const titleSpacingBelowLineMm = 15;
    const titleY = lineY + titleSpacingBelowLineMm;
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(18);
    this.pdf.text(pageTitle || '', TEMPLATE_CONSTANTS.PAGE_MARGIN_MM, titleY, { align: 'left' });
  }

  /**
   * Generate footer template for a PDF page
   */
  public generateFooter(currentPage: number, logoDataUrl: string | null = null): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const logoDataUrlToUse = logoDataUrl || this.logoDataUrl;
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const pageHeight = this.pdf.internal.pageSize.getHeight();

    // Add footer to the bottom of the page
    const footerYMm = pageHeight - 15;
    const footerLineYMm = footerYMm - 8;

    // Draw footer blue horizontal line
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(53, 123, 255);
    const footerLineStartX = 0;
    const footerLineWidth = pageWidth * 0.8;
    this.pdf.line(footerLineStartX, footerLineYMm, footerLineStartX + footerLineWidth, footerLineYMm);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Add logo next to the end of the line
    const logoWidthMm = 20;
    const logoHeightMm = 6;
    const logoSpacingMm = 3;
    const lineEndX = footerLineStartX + footerLineWidth;

    // Center logo vertically with the line
    const logoYMm = footerLineYMm - logoHeightMm / 2;
    const logoX = lineEndX + logoSpacingMm;

    // Add logo next to the end of the line
    if (logoDataUrlToUse) {
      this.pdf.addImage(logoDataUrlToUse, IMAGE_SETTINGS.PNG_FORMAT, logoX, logoYMm, logoWidthMm, logoHeightMm);
    }

    // Add page number aligned to the right
    const pageNumberText = `${currentPage}`;
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    const pageNumberY = footerLineYMm + 1;
    const pageNumberX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    this.pdf.text(pageNumberText, pageNumberX, pageNumberY, { align: 'right' });

    // Add centered text below the line
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8);
    const footerText = 'For Discussion Purposes Only - Subject to Change';
    this.pdf.text(footerText, pageWidth / 2, footerYMm, { align: 'center' });
  }

  /**
   * Execute crawl: render an iframe to capture DOM elements from a link
   */
  public async executeCrawl(
    link: { url: string; title: string },
    currentPage: number,
    totalPages: number,
    reportProgress: (progressWithinPage: number) => void
  ): Promise<HTMLCanvasElement | null> {
    const { url } = link;

    // Add page parameters to URL
    const urlWithPageParams = new URL(url);
    urlWithPageParams.searchParams.set('currentPage', currentPage.toString());
    urlWithPageParams.searchParams.set('totalPages', totalPages.toString());
    const finalUrl = urlWithPageParams.toString();

    Logger.info(`Processing page ${currentPage}/${totalPages}: ${finalUrl}`);

    // Report initial progress when starting to process a page
    reportProgress(PROGRESS_MILESTONES.INITIAL);

    // Create a hidden iframe to load the page so the user doesn't see the navigation
    const iframe = document.createElement('iframe');
    iframe.src = finalUrl;
    iframe.style.position = 'fixed';
    iframe.style.zIndex = '9999';
    iframe.style.left = '-9999px';
    iframe.style.width = '2600px';

    Logger.info('Created iframe, adding to DOM');

    document.body.appendChild(iframe);

    try {
      // Wait for iframe to load
      Logger.info('Waiting for iframe to load...');
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          Logger.error(`Timeout loading ${url}`, new Error(`Timeout loading ${url}`));
          reject(new Error(`Timeout loading ${url}`));
        }, TIMEOUT_MS.IFRAME_LOAD);

        iframe.onload = () => {
          clearTimeout(timeout);
          reportProgress(PROGRESS_MILESTONES.AFTER_IFRAME_LOAD);
          resolve();
        };

        iframe.onerror = () => {
          Logger.error(`Failed to load ${url}`, new Error(`Failed to load ${url}`));
          clearTimeout(timeout);
          reject(new Error(`Failed to load ${url}`));
        };
      });

      // Get iframe document reference
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error(`Could not access iframe content for ${url}`);
      }

      Logger.info('Waiting for API calls to complete and target element in the DOM to load...');
      reportProgress(PROGRESS_MILESTONES.BEFORE_API_WAIT);
      await this.waitForApiCallsAndPdfContentElement(iframeDoc);
      reportProgress(PROGRESS_MILESTONES.AFTER_API_COMPLETE);

      // Find the element to export
      const domElementRef = urlWithPageParams.searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET);
      const pdfContentElement = iframeDoc.querySelector(`[data-ref="${domElementRef}"]`);

      // Capture content
      reportProgress(PROGRESS_MILESTONES.CAPTURING_CONTENT);
      if (pdfContentElement) {
        const contentCanvas = await html2canvas(pdfContentElement as HTMLElement, {
          scale: 1,
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
        return contentCanvas;
      }
      Logger.info(
        `Could not find target element [${domElementRef}] in the DOM for page ${currentPage}, skipping page...`
      );
      reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
      return null;
    } catch (error) {
      Logger.error(`Error processing URL ${url}`, error);
      reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
      return null;
    } finally {
      // Clean up iframe
      Logger.info('Cleaning up iframe...');
      try {
        document.body.removeChild(iframe);
      } catch (cleanupError) {
        Logger.warn('Error cleaning up iframe:', cleanupError);
      }
    }
  }

  /**
   * Export multiple links as a single PDF
   */
  public async export(options: MultiplePagesExportOptions): Promise<MultiplePagesExportResult> {
    try {
      Logger.info('PDF Export initiated with options:', options);
      const { pages: links, onProgress, ...pdfOptions } = options;

      if (!links || links.length === 0) {
        Logger.error(
          'No links provided for multiple pages export',
          new Error('No links provided for multiple pages export')
        );
        return { success: false, error: 'No links provided for multiple pages export' };
      }

      Logger.info(`Starting export of ${links.length} links`);

      // Store canvas data for each page to create robust final PDF
      const canvases: (HTMLCanvasElement | null)[] = [];

      // Create smooth progress animator
      let globalProgressAnimator: SmoothProgressAnimator | null = null;
      if (onProgress) {
        globalProgressAnimator = new SmoothProgressAnimator((progress: number) => {
          const currentPageNum = Math.ceil((progress / 100) * links.length) || 1;
          onProgress(currentPageNum, links.length, Math.min(progress, PROGRESS_PERCENTAGE.CAP));
        });
      }

      try {
        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          const currentPage = i + 1;
          const totalPages = links.length;

          // Calculate progress base for this page (each page contributes 1/totalPages of total progress)
          const pageProgressBase = ((currentPage - 1) / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;
          const pageProgressRange = (1 / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;

          // Helper function to report progress with smooth animation
          const reportProgress = (progressWithinPage: number) => {
            const totalProgress = pageProgressBase + progressWithinPage * pageProgressRange;
            if (globalProgressAnimator) {
              globalProgressAnimator.setTarget(Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
            } else {
              onProgress?.(currentPage, totalPages, Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
            }
          };

          // Execute crawl to capture DOM elements
          const canvas = await this.executeCrawl(link, currentPage, totalPages, reportProgress);
          if (canvas) {
            canvases.push(canvas);
          }
        }
      } finally {
        // Clean up progress animator
        if (globalProgressAnimator) {
          globalProgressAnimator.cleanup();
        }
      }

      if (canvases.length === 0) {
        Logger.error('No canvas data was captured', new Error('No canvas data was captured'));
        return { success: false, error: 'No canvas data was captured' };
      }

      // Create final PDF from canvas data with proper element positioning
      Logger.info(`Creating final PDF from ${canvases.length} pages...`);

      // Initialize PDF with fixed dimensions
      this.pdf = new JsPDF({
        format: 'letter',
        orientation: 'landscape',
        compress: true,
      });

      // Load logo image once for all pages
      try {
        this.logoDataUrl = await PdfExporter.loadImageAsDataUrl('/images/logos/pdf-report-logo.png');
      } catch {
        Logger.warn('Could not load logo image, footer will be rendered without logo');
      }

      // Generate cover template on the first page
      this.generateCover(pdfOptions.coverTitle, pdfOptions.entityName, pdfOptions.dateOfIssue);

      for (let i = 0; i < canvases.length; i++) {
        const contentCanvas = canvases[i];

        // Add new page for each visited URL (cover is already on the first page)
        this.pdf.addPage();

        // Generate header template
        this.generateHeader(pdfOptions.entityName, links[i].title);

        // Add content extracted from DOM rendering in an iframe to the page
        if (contentCanvas) {
          const contentImgData = contentCanvas.toDataURL(IMAGE_SETTINGS.MIME_TYPE, IMAGE_SETTINGS.MAX_QUALITY);

          // Convert canvas dimensions from pixels to millimeters
          const canvasWidthMm = contentCanvas.width / CONVERSION_RATIOS.MM_TO_PX;
          const canvasHeightMm = contentCanvas.height / CONVERSION_RATIOS.MM_TO_PX;

          // Calculate aspect ratio
          const aspectRatio = canvasHeightMm / canvasWidthMm;

          // Fixed width of the image in millimeters
          const fixedWidthMm = IMAGE_SETTINGS.MAX_WIDTH_MM;

          // Calculate height of the image maintaining aspect ratio
          const calculatedHeightMm = fixedWidthMm * aspectRatio;

          // Determine final dimensions maintaining aspect ratio
          let contentWidthMm: number;
          let contentHeightMm: number;

          if (calculatedHeightMm > IMAGE_SETTINGS.MAX_HEIGHT_MM) {
            // If calculated height exceeds max height, use max height and recalculate width
            contentHeightMm = IMAGE_SETTINGS.MAX_HEIGHT_MM;
            contentWidthMm = contentHeightMm / aspectRatio;
          } else {
            // Otherwise, use fixed width and calculated height
            contentWidthMm = fixedWidthMm;
            contentHeightMm = calculatedHeightMm;
          }

          // Calculate content position for injection between header and footer
          const contentXPositionMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
          const contentYPositionMm = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM;

          // Content injection as an image (screenshot of the DOM element)
          this.pdf.addImage(
            contentImgData,
            IMAGE_SETTINGS.JPEG_FORMAT,
            contentXPositionMm,
            contentYPositionMm,
            contentWidthMm,
            contentHeightMm,
            IMAGE_SETTINGS.PDF_ALIAS,
            IMAGE_SETTINGS.COMPRESSION
          );

          Logger.info(`Added content to page ${i + 1}`);
        }

        // Generate footer template
        this.generateFooter(i + 1, this.logoDataUrl);

        Logger.info(`Completed page ${i + 1}`);
      }

      // Download the final PDF
      const filename = pdfOptions.filename || 'multiple-pages-export';
      Logger.info(`Downloading final PDF: ${filename}.pdf`);

      try {
        this.pdf.save(`${filename}.pdf`);
        Logger.info('PDF save() method called successfully');
      } catch (saveError) {
        Logger.error('Error calling finalPdf.save():', saveError);
        // Continue anyway - the PDF was created, even if save failed
        // The error might be due to browser restrictions or popup blockers
      }

      // Wait for download to initiate with timeout protection
      // Note: We cannot reliably detect if a browser download actually started
      // due to browser security restrictions, but we can handle errors from save()
      // If the download doesn't start (e.g., blocked by browser), we still report success
      // because the PDF was generated successfully - the download failure is a browser limitation
      await new Promise<void>(resolve => {
        const startTime = Date.now();

        setTimeout(() => {
          const elapsed = Date.now() - startTime;

          // If we waited longer than the maximum expected time, log a warning
          if (elapsed > TIMEOUT_MS.DOWNLOAD_MAX_WAIT) {
            Logger.warn(
              `Wait time (${elapsed}ms) exceeded maximum (${TIMEOUT_MS.DOWNLOAD_MAX_WAIT}ms). ` +
                'Download may have been blocked by browser or popup blocker, but PDF was generated successfully.'
            );
          } else {
            Logger.info('PDF download should have initiated');
          }

          // Report completion - the PDF was generated successfully
          // Note: Even if download was blocked, the PDF creation succeeded
          if (globalProgressAnimator) {
            globalProgressAnimator.setImmediate(PROGRESS_PERCENTAGE.MAX);
          } else {
            onProgress?.(links.length, links.length, PROGRESS_PERCENTAGE.MAX);
          }
          resolve();
        }, TIMEOUT_MS.DOWNLOAD_DELAY);
      });

      Logger.info('Multiple pages export completed successfully');
      return {
        success: true,
        filename: `${filename}.pdf`,
        totalPages: links.length,
        processedPages: canvases.length,
      };
    } catch (error) {
      Logger.error('Error in export:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred during multiple pages export';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
